import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  #suppressMoreActivation = false;

  connectedCallback() {
    super.connectedCallback();

    for (const overflowMenu of [this.shadowOverflowMenu, this.externalOverflowMenu]) {
      overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
        signal: this.#abortController.signal,
      });
    }

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the active overflow menu.
   */
  get overflowMenu() {
    return this.externalOverflowMenu?.querySelector('[slot="overflow"]')
      ? this.externalOverflowMenu
      : this.shadowOverflowMenu;
  }

  /** @returns {HTMLElement | null} */
  get shadowOverflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /** @returns {HTMLElement | null} */
  get externalOverflowMenu() {
    const overflowMenu = this.querySelector('[data-menu-overflow-portal]');
    return overflowMenu instanceof HTMLElement ? overflowMenu : null;
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return (
      (this.refs.overflowMenu?.matches(':hover') ?? false) ||
      (this.externalOverflowMenu?.matches(':hover') ?? false)
    );
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (
      this.#suppressMoreActivation &&
      event.target instanceof Element &&
      event.target.closest('[slot="more"]')
    ) {
      this.#suppressMoreActivation = false;
      return;
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const sourceItem = event.target.closest('.menu-list__list-item');
    if (!(sourceItem instanceof HTMLElement)) return;

    const opensOverflowMenu = sourceItem.slot === 'overflow' || sourceItem.slot === 'more';
    const item =
      sourceItem.slot === 'more'
        ? findMenuItem(this.#getFirstOverflowItem())
        : findMenuItem(sourceItem);

    if (!item || item == this.#state.activeItem) return;

    this.dataset.overflowExpanded = opensOverflowMenu.toString();
    this.#setMoreExpanded(opensOverflowMenu);

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && opensOverflowMenu) {
      submenu = this.overflowMenu;
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const sourceItem = event.target.closest('.menu-list__list-item');
    if (!(sourceItem instanceof HTMLElement)) return;

    const item =
      sourceItem.slot === 'more'
        ? findMenuItem(this.#getFirstOverflowItem())
        : findMenuItem(sourceItem);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Provide predictable keyboard entry, exit, and dismissal for the overflow popup.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (!(event.target instanceof Element)) return;

    const sourceItem = event.target.closest('.menu-list__list-item');
    if (!(sourceItem instanceof HTMLElement)) return;

    const originalTarget = event.composedPath()[0];
    let focusables = this.#getOverflowFocusables();

    if (sourceItem.slot === 'more') {
      const entersPopup =
        event.key === 'ArrowDown' ||
        event.key === 'Enter' ||
        event.key === ' ' ||
        (event.key === 'Tab' && !event.shiftKey);

      if (entersPopup && !focusables.length) {
        this.#debouncedActivateHandler.cancel();
        this.#activateHandler(event);
        focusables = this.#getOverflowFocusables();
      }

      const firstFocusable = focusables[0];
      if (entersPopup && firstFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }

      return;
    }

    if (sourceItem.slot !== 'overflow' || !(originalTarget instanceof HTMLElement)) return;

    const firstFocusable = focusables[0];
    const lastFocusable = focusables[focusables.length - 1];

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#deactivate(this.#state.activeItem, true);
      this.#focusMoreWithoutOpening();
      return;
    }

    if (event.key !== 'Tab') return;

    if (event.shiftKey && originalTarget === firstFocusable) {
      event.preventDefault();
      this.#getMoreButton()?.focus();
    } else if (!event.shiftKey && originalTarget === lastFocusable) {
      event.preventDefault();
      const nextFocusable = this.#getFocusableAfterPopup();
      this.#deactivate(this.#state.activeItem, true);
      nextFocusable?.focus();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#setMoreExpanded(false);

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Keep the visible More trigger's popup state available to assistive technology.
   * @param {boolean} expanded
   */
  #setMoreExpanded(expanded) {
    const moreButton = this.#getMoreButton();
    if (moreButton) {
      moreButton.ariaExpanded = expanded.toString();
    }
  }

  /** @returns {HTMLButtonElement | null} */
  #getMoreButton() {
    const moreButton = this.querySelector('[data-overflow-list-more] button, [slot="more"] button');
    return moreButton instanceof HTMLButtonElement ? moreButton : null;
  }

  /** @returns {HTMLElement | null} */
  #getFirstOverflowItem() {
    const item = this.querySelector('.menu-list__list-item[slot="overflow"]');
    return item instanceof HTMLElement ? item : null;
  }

  /** @returns {HTMLElement[]} */
  #getOverflowFocusables() {
    const elements = this.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    return [...elements].filter(
      (element) =>
        element instanceof HTMLElement &&
        element.closest('[slot="overflow"]') &&
        element.getClientRects().length > 0 &&
        getComputedStyle(element).visibility !== 'hidden'
    );
  }

  /** @returns {HTMLElement | null} */
  #getFocusableAfterPopup() {
    const moreButton = this.#getMoreButton();
    if (!moreButton) return null;

    const focusables = [...document.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter(
      (element) =>
        element instanceof HTMLElement &&
        element.getClientRects().length > 0 &&
        getComputedStyle(element).visibility !== 'hidden'
    );
    const popupIndexes = [moreButton, ...this.#getOverflowFocusables()].map((element) => focusables.indexOf(element));
    const popupEndIndex = Math.max(...popupIndexes);
    const nextFocusable = focusables[popupEndIndex + 1];
    return nextFocusable instanceof HTMLElement ? nextFocusable : null;
  }

  #focusMoreWithoutOpening() {
    const moreButton = this.#getMoreButton();
    if (!moreButton) return;

    this.#suppressMoreActivation = true;
    moreButton.focus();
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.classList.contains('header-menu--split')
      ? this.querySelectorAll(
          '.header-menu__split-content img[loading="lazy"], .header-menu__continuation-portal img[loading="lazy"]'
        )
      : this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
